import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  Col,
  Input,
  Row,
  Table,
} from "reactstrap";
import { useSelector } from "react-redux";
import FalconCardHeader from "../common/FalconCardHeader";
import {
  createProperty,
  deleteOneProperty,
  fetchProperties,
} from "../../helpers/api/condominium";
import ButtonIcon from "../common/ButtonIcon";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import withRedirect from "../../helpers/hoc/withRedirect";
import { Toast } from "./../UI/toast/Toast";
import {
  faFileExcel,
  faPlus,
  faKey,
} from "@fortawesome/free-solid-svg-icons";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import FullPagination from "../common/pagination/FullPagination";
import { isIterableArray } from "../../helpers/utils";
import PropertyListItem from "../common/PropertyListItem";
import { Or, PreloadState } from "../UI/preloading/PreloadState";
import SupportLink from "../root/support/components/SupportLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

import OnboardingBanner from "../onboarding/OnboardingBanner";
import useOnboardingStepParam from "../../helpers/hooks/useOnboardingStepParam";

// Modal Subcomponents
import AddPropertyModal from "./AddPropertyModal";
import ImportPropertiesModal from "./ImportPropertiesModal";
import InitialLoadModal from "./InitialLoadModal";
import VoidInitialDebtModal from "./VoidInitialDebtModal";
import NotifyCredentialsModal from "./NotifyCredentialsModal";
import LinkOwnerModal from "./LinkOwnerModal";

// HO-HU-36: este modulo atiende dos pasos de la guia de primeros pasos, y cada
// uno abre una plantilla distinta del mismo modal de importacion.
const ONBOARDING_IMPORT_TYPES = {
  owners: "properties",
  balances: "receivable",
};

const Properties = ({ setRedirect, setRedirectUrl }) => {
  const { selectedCondominium, condominium } = useSelector(
    (state) => state.condominium
  );
  const [textSearch, setTextSearch] = useState("");
  const [params, setParams] = useState({
    condominiumId: selectedCondominium,
    search: null,
  });

  useEffect(() => {
    setParams((prev) =>
      prev.condominiumId === selectedCondominium
        ? prev
        : { ...prev, condominiumId: selectedCondominium }
    );
  }, [selectedCondominium]);

  const applyPropertySearch = (value = textSearch) => {
    const term = typeof value === "string" ? value.trim() : "";
    setTextSearch(term);
    setParams((prev) => ({ ...prev, search: term || null }));
  };
  const {
    data,
    error,
    isLoading,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchProperties, params);
  const [collapseOne, collapseOneOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importType, setImportType] = useState("properties");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [notifyTarget, setNotifyTarget] = useState(null);
  const [linkOwnerOpen, setLinkOwnerOpen] = useState(false);
  const [linkOwnerTarget, setLinkOwnerTarget] = useState(null);
  const [voidDebtTarget, setVoidDebtTarget] = useState(null);
  const [voidAllOpen, setVoidAllOpen] = useState(false);

  const renderProvidersItems = () => {
    return data.map((property, i) => {
      return (
        <PropertyListItem
          key={i.toString()}
          propertyId={property.id}
          name={`${property?.user?.data?.firstname} ${property?.user?.data?.lastname}`}
          phone={property?.user.data.phone}
          username={property?.user?.username}
          onDelete={() => handleDelete(property.id)}
          onEdit={() => handleeSeeDetails(property.id)}
          onNotifyCredentials={() => {
            setNotifyTarget({ ids: [property.id], name: property.property });
            setConfirmModalOpen(true);
          }}
          onLinkOwner={() => {
            setLinkOwnerTarget(property);
            setLinkOwnerOpen(true);
          }}
          hasInitialDebt={Boolean(property.balance?.has_initial_debt)}
          onVoidInitialDebt={() => setVoidDebtTarget(property.id)}
          alternativeName="propietario"
          property={property.property}
          balance={property.balance?.balance}
          netBalance={property.balance?.net_balance}
          debt={property.balance?.pending_debts_sum}
          aliquot={property.aliquot?.value}
          options={true}
        />
      );
    });
  };

  const handleeSeeDetails = async (property) => {
    setRedirectUrl(`/dashboard/properties/${property}`);
    setRedirect(true);
  };

  const handleCreate = async (quantity) => {
    try {
      const { code } = await createProperty(selectedCondominium, quantity);
      setRedirectUrl("/dashboard/properties/");
      setRedirect(true);
      if (code === SUCCESS_RESPONSE.ADDED_TO_QUEUE) {
        Toast.fire({
          type: "success",
          title:
            quantity === 1
              ? "Se ha añadido el propietario a la cola de añadidos."
              : "Se han añadido los propietarios a la cola de añadidos.",
        });
        setRedirectUrl("/dashboard/properties/");
        setRedirect(true);
      }
      if (code === SUCCESS_RESPONSE.CREATED) {
        Toast.fire({
          type: "success",
          title:
            quantity === 1
              ? "Se ha añadido el propietario satisfactoriamente."
              : "Se han añadido los propietarios satisfactoriamente.",
        });
        setRedirectUrl("/dashboard/properties/");
        setRedirect(true);
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleDelete = async (property) => {
    try {
      const { code } = await deleteOneProperty(property);
      if (code === SUCCESS_RESPONSE.OK) {
        Toast.fire({
          type: "success",
          title: "Se ha eliminado el propietario satisfactoriamente.",
        });
        setRedirectUrl("/dashboard/properties/");
        setRedirect(true);
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const [initialLoadOpen, setInitialLoadOpen] = useState(false);

  const toggleImportModal = (type = "properties") => {
    setImportType(type);
    setImportModalOpen(!importModalOpen);
  };

  // HO-HU-36: al llegar desde la guia de primeros pasos, el banner del paso
  // ofrece la plantilla de Excel como un boton, pero NO se abre el modal solo.
  //
  // En esta pantalla los dos pasos que atiende la guia (propietarios y saldos
  // iniciales) tienen tambien un camino uno-a-uno --"Añadir propietario" y el
  // boton "Registrar Saldo" de cada fila--, que para pocos inmuebles es el
  // razonable. Abrir el modal de importacion de entrada daba a entender que
  // el Excel era el unico camino.
  const onboardingStep = useOnboardingStepParam();
  const onboardingImportType = ONBOARDING_IMPORT_TYPES[onboardingStep];

  return (
    <>
      <PageHeader
        title={`Propietarios`}
        titleTag="h2"
        description="Gestiona los propietarios del condominio."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      <OnboardingBanner
        steps={["owners", "balances"]}
        action={
          onboardingImportType
            ? {
                label: "Hacer la carga inicial",
                onClick: () => setInitialLoadOpen(true),
              }
            : undefined
        }
      />
      <Card>
        <FalconCardHeader title="Propietarios">
          <Row>
            <Col xs={12} className="d-flex">
              <Input
                type="text"
                value={textSearch}
                onKeyPress={(e) => {
                  if (e.key === "Enter") applyPropertySearch(e.target.value);
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  setTextSearch(value);
                  if (!value.trim()) {
                    setParams((prev) => ({ ...prev, search: null }));
                  }
                }}
                placeholder="Buscar por inmueble, nombre, usuario, cédula, teléfono o correo..."
                className="d-inline-block flex-grow-1 mr-2"
                style={{ flex: 1 }}
              />
              <Button
                className="d-inline-block"
                color="falcon-default"
                onClick={() => applyPropertySearch()}
              >
                Buscar
              </Button>
            </Col>
            <Col xs={12} className="mt-3 text-center text-md-right">
              <ButtonIcon
                icon={faPlus}
                color="falcon-default"
                onClick={() => collapseOneOpen(!collapseOne)}
              >
                Añadir propietario
              </ButtonIcon>

              <ButtonIcon
                icon={faKey}
                color="falcon-default"
                className="ml-2"
                onClick={() => {
                  setNotifyTarget(null);
                  setConfirmModalOpen(true);
                }}
              >
                Notificar accesos
              </ButtonIcon>

              <UncontrolledDropdown className="d-inline-block ml-2 mt-2 mt-sm-0">
                <DropdownToggle
                  color="falcon-default"
                  caret
                >
                  <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                  Cargar archivo
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={() => setInitialLoadOpen(true)}>
                    Carga inicial (todo en un archivo)
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={() => toggleImportModal("properties")}>
                    Importar Propietarios
                  </DropdownItem>
                  <DropdownItem onClick={() => toggleImportModal("advance")}>
                    Importar Saldos a Favor Masivos
                  </DropdownItem>
                  <DropdownItem onClick={() => toggleImportModal("receivable")}>
                    Importar Cuentas por Cobrar
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Col>
          </Row>
        </FalconCardHeader>
        <CardBody className={isIterableArray(data) ? "p-0 fs--1" : "fs--1"}>
          <PreloadState cond={isIterableArray(data)} loading={isLoading}>
            <Or value={true}>
              <Table responsive striped>
                <thead>
                  <tr>
                    <th width="40%" className="align-middle">
                      Usuario y nombre
                    </th>
                    <th className="align-middle">Teléfono</th>
                    <th className="text-center align-middle">
                      Saldo
                    </th>
                    <th className="text-center align-middle">
                      Alicuota
                    </th>

                    <th width="10%" className="text-center align-middle">
                      Registrar Saldo
                    </th>

                    <th width="10%" className="text-center align-middle">
                      Opciones
                    </th>
                  </tr>
                </thead>
                <tbody>{renderProvidersItems()}</tbody>
              </Table>
            </Or>
            <Or value={false} cond={!error}>
              <Or value={true}>
                <Row className="p-card">
                  <Col>
                    <Alert color="info" className="mb-0">
                      Aún no has creado un propietario, puedes crear uno ahora
                      mismo pulsando el botón &quot;Añadir propietario&quot;
                    </Alert>
                  </Col>
                </Row>
              </Or>
              <Or value={false} cond={!error}>
                <Or value={true}>
                  <Row className="p-card">
                    <Col>
                      <Alert color="info" className="mb-0">
                        Aún no has creado un propietario, puedes crear uno ahora
                        mismo pulsando el botón &quot;Añadir propietario&quot;
                      </Alert>
                    </Col>
                  </Row>
                </Or>
                <Or value={false}>
                  <Row className="p-card">
                    <Col>
                      <Alert color="danger" className="mb-0">
                        Esta página no está disponible
                        <Button
                          data-testid="Button"
                          color="link"
                          size="sm"
                          className="ml-1 pl-0"
                          onClick={() => {
                            setRedirectUrl(`/dashboard/banks`);
                            setRedirect(true);
                          }}
                        >
                          Volver
                        </Button>
                      </Alert>
                    </Col>
                  </Row>
                </Or>
              </Or>
            </Or>
          </PreloadState>
        </CardBody>
        {isIterableArray(data) && (
          <CardFooter>
            <FullPagination
              nextPage={nextPage}
              prevPage={previousPage}
              goToPage={goToPage}
              meta={meta}
              className={"shadow-none"}
              posText={"propietarios"}
              perPage={perPage}
            />
          </CardFooter>
        )}
      </Card>
      <AddPropertyModal
        isOpen={collapseOne}
        toggle={() => collapseOneOpen(!collapseOne)}
        condominiumName={condominium}
        onAdd={handleCreate}
      />

      <InitialLoadModal
        isOpen={initialLoadOpen}
        toggle={() => setInitialLoadOpen(false)}
        selectedCondominium={selectedCondominium}
        onSuccess={() => setParams((prevParams) => ({ ...prevParams }))}
        onVoidAll={() => setVoidAllOpen(true)}
      />
      <VoidInitialDebtModal
        isOpen={voidAllOpen}
        toggle={() => {
          setVoidAllOpen(false);
          setInitialLoadOpen(true);
        }}
        condominiumId={selectedCondominium}
        onVoided={() => setParams((prevParams) => ({ ...prevParams }))}
      />
      <VoidInitialDebtModal
        isOpen={Boolean(voidDebtTarget)}
        toggle={() => setVoidDebtTarget(null)}
        condominiumId={selectedCondominium}
        propertyId={voidDebtTarget}
        onVoided={() => setParams((prevParams) => ({ ...prevParams }))}
      />
      <ImportPropertiesModal
        isOpen={importModalOpen}
        toggle={() => setImportModalOpen(!importModalOpen)}
        importType={importType}
        selectedCondominium={selectedCondominium}
        onSuccess={() => setParams((prevParams) => ({ ...prevParams }))}
      />

      <NotifyCredentialsModal
        isOpen={confirmModalOpen}
        toggle={() => setConfirmModalOpen(!confirmModalOpen)}
        selectedCondominium={selectedCondominium}
        propertyIds={notifyTarget ? notifyTarget.ids : null}
        propertyName={notifyTarget ? notifyTarget.name : null}
      />

      <LinkOwnerModal
        isOpen={linkOwnerOpen}
        toggle={() => setLinkOwnerOpen(false)}
        property={linkOwnerTarget}
        onLinked={() => setParams((prevParams) => ({ ...prevParams }))}
      />
    </>
  );
};

Properties.propTypes = { location: PropTypes.object.isRequired };

export default withRedirect(Properties);
